import { Modal, ModalProps } from './Modal';
import { theme } from '../theme';
import { render as reactDOMRender, unmountComponentAtNode } from 'react-dom';
import {
  ButtonProps,
  ChakraProvider,
  ColorModeScript,
  CSSReset,
} from '@chakra-ui/react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ModalFunc = (...args: any[]) => any;

export interface ModalFunctionProps
  extends Omit<ModalProps, 'isOpen' | 'onClose' | 'children'> {
  visible?: boolean;
  closable?: boolean;
  onOk?: ModalFunc;
  onCancel?: ModalFunc;
  onClose?: ModalFunc;
  icon?: React.ReactNode;
  content?: React.ReactNode;
  okText?: React.ReactNode;
  okType?: ButtonProps['variant'];
  cancelText?: React.ReactNode;
  showOk?: boolean;
  showCancel?: boolean;
  okButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
}

export const ModalFunction = (props: ModalFunctionProps) => {
  let config: ModalFunctionProps = {
    okText: 'Ok',
    okType: 'solid',
    cancelText: 'Cancel',
    showOk: true,
    showCancel: false,
    visible: true,
    closable: false,
    ...props,
  };

  const container = document.createDocumentFragment();

  const destroy = () => {
    unmountComponentAtNode(container);
    config = { ...config, visible: false };
    config.onClose && config.onClose();
    render(config);
  };
  const update = (updateProps: ModalFunctionProps) => {
    config = { ...config, ...updateProps };
    render(config);
  };

  const handleClose = () => {
    destroy();
    config.onClose && config.onClose();
  };
  const handleOk = () => {
    destroy();
    config.onOk && config.onOk();
  };
  const handleCancel = () => {
    destroy();
    config.onCancel && config.onCancel();
  };

  const render = (config: ModalFunctionProps) => {
    const {
      visible = true,
      closable,
      icon,
      header,
      content,
      headerProps,
      contentProps,
      onClose: _onClose,
      ...rest
    } = config;

    setTimeout(() =>
      reactDOMRender(
        <>
          <ColorModeScript initialColorMode={theme.config.initialColorMode} />
          <ChakraProvider theme={theme}>
            <Modal
              isOpen={visible}
              onClose={handleClose}
              portalProps={{}}
              showDivider={false}
              showCloseButton={closable}
              onOk={handleOk}
              onCancel={handleCancel}
              icon={icon}
              header={header}
              headerProps={{
                fontWeight: 'semibold',
                lineHeight: '1.4',
                mb: 2,
                color: 'blackAlpha.500',
                pb: 0,
                ...headerProps,
              }}
              contentProps={{ fontSize: 'sm', ...contentProps }}
              bodyProps={{ pb: 0 }}
              {...rest}
            >
              {content}
            </Modal>
            <CSSReset />
          </ChakraProvider>
        </>,
        container,
      ),
    );
  };

  render(config);

  return {
    destroy: destroy,
    update: update,
  };
};
